import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DataRef, DataRefsByKey, Status, Unit } from '@shared/models/dataref.model';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { RecipeService } from '@services/recipe/recipe.service';
import { TripletIdLite } from '@shared/models/generic/id/triplet-id-lite.model';
import { NotificationService } from '@services/notification.service';
import { RefDataService } from '@services/ref-data.service';
import { ConfigService } from 'src/app/services/config.service';

@Component({
  selector: 'app-new-variant-tab',
  templateUrl: './new-variant-tab.component.html',
  styleUrls: ['./new-variant-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class NewVariantTabComponent implements OnChanges {
  @Input() existingVariants?: { label: string; id: TripletIdLite }[];
  @Input() creationStatus?: Status;
  @Input() selectedYieldUnit?: Unit | null;
  @Input() yieldUnitsList?: DataRefsByKey<Unit> | null;
  @Input() variantDomain?: string | null;
  @Input() variantMarket?: string | undefined;
  @Input() variantLang?: string | undefined;

  @Output() newVariantCreated = new EventEmitter<string>();

  formGroup?: UntypedFormGroup;
  yieldUnitsListArray: Unit[] = [];

  constructor(
    private readonly recipeService: RecipeService,
    private readonly notificationService: NotificationService,
    private refDataService: RefDataService,
    private configService: ConfigService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (this.yieldUnitsList && changes.selectedYieldUnit) {
      // fournee + fournee XL listed in Cake factory, multiple choice in list
      if (this.variantDomain && this.configService.getAppConfig().multiUnitDomainApps[this.variantDomain]) {
        const domainUnitsList = this.configService.getAppConfig().multiUnitDomainApps[this.variantDomain];
        this.yieldUnitsListArray = this.refDataService
          .toArray(this.yieldUnitsList)
          .filter((unit: DataRef) => domainUnitsList.includes(unit.key)) as Unit[];
      } else {
        // normal case, 1 unit in list
        this.yieldUnitsListArray = [changes.selectedYieldUnit?.currentValue];
      }
    }
    if (changes.selectedYieldUnit && this.selectedYieldUnit) {
      this.formGroup = new UntypedFormGroup({
        yieldQuantity: new UntypedFormControl(undefined, Validators.compose([Validators.required, Validators.min(0)])),
        yieldUnit: new UntypedFormControl(
          { value: this.selectedYieldUnit.key, disabled: this.yieldUnitsListArray.length < 2 },
          Validators.required,
        ),
        duplicateVariant: new UntypedFormControl(undefined, Validators.required),
      });
    }
  }

  createVariant(): void {
    if (this.formGroup?.invalid) {
      return;
    }

    this.recipeService
      .createYieldVariant(this.formGroup?.value.duplicateVariant, {
        quantity: this.formGroup?.value.yieldQuantity,
        unit: this.formGroup?.value.yieldUnit ?? this.selectedYieldUnit?.key,
        quantityDisplay: `${this.formGroup?.value.yieldQuantity}`,
      })
      .subscribe(
        (createdVariantId) => {
          this.newVariantCreated.emit(createdVariantId.functionalId);
        },
        (error) => this.notificationService.sendAPIError(error),
      );
  }
}
